if (!window.__PlanBuilderController) {
  const SECTION_ORDER = ["breakfast", "meals", "snacks", "extras"];

  // Stands in for a section key we do not know. A cart line records the meal and its
  // delivery but not the category it was picked from, so restoreSelections() resolves
  // the category from whichever section actually renders that variant.
  const ANY_SECTION = "*";

  // The first cut of the delivery-date picker wrote these cart attributes. They
  // were replaced by batch_1_delivery_date / batch_2_delivery_date (Sunday run /
  // Wednesday run — see DeliverySchedule.cartAttributes). Carts still carrying
  // the old keys get them blanked on the next save; the old date is honoured
  // on load so an in-flight cart keeps its pick.
  const LEGACY_DELIVERY_ATTRIBUTE_KEYS = ["delivery_date", "delivery_date_2", "delivery_day"];

  class PlanBuilderController {
    constructor(root) {
      this.root = root;
      this.config = this.parseConfig();
      // Email campaign visitors see the campaign's percent (snippets/campaign-offer.liquid).
      const campaign = window.GoodPrepCampaignOffer;
      if (campaign && this.config?.introOffer?.percent > 0) this.config.introOffer.percent = campaign.percent;
      this.i18n = this.parseI18n();
      this.state = this.buildInitialState();

      // Without a config there is no state, and every render below dereferences
      // it. Bail before any listener is attached: a half-built controller would
      // still be assigned to root.__controller, so destroy() could never run and
      // the document-level listeners would leak on section reload.
      if (!this.config || !this.state) {
        console.error("[plan-builder] missing or invalid config; section not initialised");
        return;
      }

      this.activeFilters = new Set();
      // Tracks "delivery:category" pairs we've already auto-advanced from, so
      // returning to a completed tab to edit doesn't bounce the user forward.
      this._advancedCategories = new Set();
      // Delivery indexes already pointed at once via the "next delivery" nudge.
      this._nudgedDeliveries = new Set();

      this._onClick = this._onClick.bind(this);
      this._onChange = this._onChange.bind(this);
      this._onKeydown = this._onKeydown.bind(this);
      this._onDocumentClick = this._onDocumentClick.bind(this);
      this._onResize = () => this.updateScrollbars();
      this._onWindowScroll = this._onWindowScroll.bind(this);
      this._onSectionToggle = this._onSectionToggle.bind(this);
      this.root.addEventListener("click", this._onClick);
      this.root.addEventListener("change", this._onChange);
      document.addEventListener("keydown", this._onKeydown);
      // Root-level clicks never see the page background, so dismissing the
      // filter panel needs its own document-level listener.
      document.addEventListener("click", this._onDocumentClick);
      window.addEventListener("resize", this._onResize);
      // Opening a <details> is native, but the rest of the UI still needs to
      // know which category the user landed on. `toggle` does not bubble, so
      // this listens in the capture phase rather than per section.
      this.root.addEventListener("toggle", this._onSectionToggle, true);

      this.expose();
      this.emit();
      this.applyActiveDelivery();
      this.applyActiveCategory();
      // Before updateFooter/updateOverview: every date label on the page reads
      // from the state this sets.
      this.initDeliveryDate();
      this.updateFooter();
      this.updateOverview();
      // After the initial UI pass — restore repaints via refreshUi().
      this.restoreSelections();
      this.updateCheckoutCtaStates();
      this.updateDeliveryTabs();
      this.updateSectionHeader();
      this.updateCategoryTabs();

      // The desktop sidebar scrolls on its own, but page scrolling should move
      // it too, so the total and macros at its foot come into view without
      // hovering the panel.
      this._overviewPanel = this.root.querySelector(".plan-builder__overview--sheet");
      this._desktopQuery = window.matchMedia("(min-width: 990px)");
      this._lastScrollY = window.scrollY;
      window.addEventListener("scroll", this._onWindowScroll, { passive: true });
    }

    // Mirrors the page's scroll delta onto the sidebar. The browser clamps
    // scrollTop, so the panel simply stops at either end. Desktop only: on
    // mobile the same element is the bottom sheet, which must stay put.
    _onWindowScroll() {
      const delta = window.scrollY - this._lastScrollY;
      this._lastScrollY = window.scrollY;
      if (!this._overviewPanel || !this._desktopQuery.matches) return;
      this._overviewPanel.scrollTop += delta;
    }

    destroy() {
      window.removeEventListener("scroll", this._onWindowScroll);
      this.root.removeEventListener("click", this._onClick);
      this.root.removeEventListener("change", this._onChange);
      document.removeEventListener("keydown", this._onKeydown);
      document.removeEventListener("click", this._onDocumentClick);
      window.removeEventListener("resize", this._onResize);
      this.root.removeEventListener("toggle", this._onSectionToggle, true);
      // The deep-link reconciler swaps this section wholesale; a trailing save must not
      // fire against a dead controller.
      clearTimeout(this._saveTimer);
    }

    // A <summary> tap opened a section; mirror it into activeCategory so the
    // tabs, the desktop header and auto-advance all agree. Closing is ignored:
    // the sibling that just opened fires its own toggle, and setActiveCategory
    // no-ops when the key is unchanged, so there is no feedback loop with
    // applyActiveCategory().
    _onSectionToggle(event) {
      const sectionEl = event.target;
      if (!sectionEl.open) return;
      // The shared Add Ons panel is not a per-delivery section, but opening it
      // (a mobile accordion tap) selects the "extras" category all the same.
      // keepSections: this panel renders after every delivery, so collapsing
      // them would delete the whole page above the row just tapped. See
      // applyActiveCategory().
      if (sectionEl.matches?.("[data-pb-addons]")) {
        this.setActiveCategory("extras", { keepSections: true });
        return;
      }
      if (!sectionEl.matches?.("[data-pb-section]")) return;
      this.setActiveCategory(sectionEl.dataset.sectionKey);
    }

    _onKeydown(event) {
      if (event.key !== "Escape") return;
      this.closeInfoModal();
      // Dismiss only — Escape must not discard the user's filter selection.
      this.closeFilter();
    }

    // Dismisses the filter panel when the click lands outside the whole
    // filter widget (toggle included, so opening it doesn't immediately
    // re-close). Selections are kept: only the X clears them.
    _onDocumentClick(event) {
      const panel = this.root.querySelector("[data-pb-filter-panel]");
      if (!panel || panel.hasAttribute("hidden")) return;
      if (event.target.closest("[data-pb-filter]")) return;
      this.closeFilter();
    }

    _onChange(event) {
      const filterInput = event.target.closest("[data-pb-filter-key]");
      if (filterInput) {
        this.applyFilters();
      }
    }

    parseConfig() {
      const node = this.root.querySelector("[data-plan-builder-config]");
      if (!node) return null;
      try {
        return JSON.parse(node.textContent.trim());
      } catch (e) {
        console.error("[plan-builder] failed to parse config", e);
        return null;
      }
    }

    parseI18n() {
      const node = this.root.querySelector("[data-plan-builder-i18n]");
      if (!node) return {};
      try {
        const raw = JSON.parse(node.textContent.trim());
        // Locale values whose key ends in `_html` are emitted unescaped by
        // Shopify's `t` filter. Other values may include HTML-escaped chars
        // (& -> &amp; etc.); decode common entities here as a safety net.
        const decode = (s) => (typeof s === "string"
          ? s.replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"').replace(/&#39;/g, "'")
          : s);
        return Object.keys(raw).reduce((acc, k) => { acc[k] = decode(raw[k]); return acc; }, {});
      } catch (e) {
        console.error("[plan-builder] failed to parse i18n", e);
        return {};
      }
    }

    // Replace upper-case __PLACEHOLDER__ tokens in a translated string.
    // The Liquid `t` filter renders __KEY__ verbatim when the locale value
    // contains literal `{{ key }}` interpolations passed in via | t: key: '__KEY__'.
    fmt(key, vars = {}) {
      let str = this.i18n[key] || "";
      Object.keys(vars).forEach((k) => {
        str = str.split(`__${k.toUpperCase()}__`).join(vars[k]);
      });
      return str;
    }

    buildInitialState() {
      const c = this.config;
      if (!c) return null;
      const cols = c.collections;
      const blankSel = (key) => ({
        collectionId: cols[key]?.id ?? null,
        collectionHandle: cols[key]?.handle ?? null,
        name: cols[key]?.name ?? key,
        selections: [],
      });
      return {
        plan: c.plan,
        daysPerWeek: c.daysPerWeek,
        mealsPerDay: c.mealsPerDay,
        hasBreakfast: c.hasBreakfast,
        hasSnacks: c.hasSnacks,
        // Older cached configs predate hasExtras; treat missing as present.
        hasExtras: c.hasExtras !== false,
        activeDeliveryIndex: 0,
        activeCategory: c.hasBreakfast ? "breakfast" : "meals",
        // ISO "YYYY-MM-DD" in the store's calendar; set by initDeliveryDate()
        // once delivery-schedule.js has been consulted. The second box is null
        // for single-box plans. deliveryDay is "sunday" | "wednesday".
        firstDeliveryDate: null,
        secondDeliveryDate: null,
        deliveryDay: null,
        deliveries: c.deliveries.map((d) => ({
          label: d.label,
          dayCount: d.dayCount,
          bundle: d.bundle,
          collections: {
            breakfast: blankSel("breakfast"),
            meals: blankSel("meals"),
            snacks: blankSel("snacks"),
            extras: blankSel("extras"),
          },
        })),
      };
    }

    _onClick(event) {
      const filterToggle = event.target.closest("[data-pb-filter-toggle]");
      if (filterToggle) {
        event.preventDefault();
        this.toggleFilter();
        return;
      }
      const filterClose = event.target.closest("[data-pb-filter-close]");
      if (filterClose) {
        event.preventDefault();
        this.closeFilter();
        return;
      }
      const filterClear = event.target.closest("[data-pb-filter-clear]");
      if (filterClear) {
        event.preventDefault();
        this.clearFilters();
        return;
      }
      const dateTrigger = event.target.closest("[data-pb-date-trigger]");
      if (dateTrigger) {
        event.preventDefault();
        this.openDatePicker();
        return;
      }
      const deliveryTab = event.target.closest("[data-pb-delivery-tab]");
      if (deliveryTab) {
        event.preventDefault();
        this.setActiveDelivery(parseInt(deliveryTab.dataset.deliveryIndex, 10));
        return;
      }
      const categoryTab = event.target.closest("[data-pb-category-tab]");
      if (categoryTab) {
        event.preventDefault();
        this.setActiveCategory(categoryTab.dataset.sectionKey);
        return;
      }
      const checkoutBtn = event.target.closest("[data-pb-footer-continue], [data-pb-overview-continue]");
      if (checkoutBtn) {
        event.preventDefault();
        this.handleCheckout();
        return;
      }
      const errorClose = event.target.closest("[data-pb-error-close]");
      if (errorClose) {
        event.preventDefault();
        this.closeErrorModal();
        return;
      }
      const errorRetry = event.target.closest("[data-pb-error-retry]");
      if (errorRetry) {
        event.preventDefault();
        this.closeErrorModal();
        this.handleCheckout();
        return;
      }
      const infoBtn = event.target.closest("[data-pb-info]");
      if (infoBtn) {
        event.preventDefault();
        this.openInfoModal(infoBtn);
        return;
      }
      const infoClose = event.target.closest("[data-pb-info-close]");
      if (infoClose) {
        event.preventDefault();
        this.closeInfoModal();
        return;
      }
      const addBtn = event.target.closest("[data-pb-add]");
      if (addBtn) {
        event.preventDefault();
        this.handleAdd(addBtn);
        return;
      }
      const stepBtn = event.target.closest("[data-pb-step]");
      if (stepBtn) {
        event.preventDefault();
        const dir = parseInt(stepBtn.dataset.pbStep, 10);
        this.handleStep(stepBtn, dir);
        return;
      }
      const removeFromOverview = event.target.closest("[data-pb-overview-remove]");
      if (removeFromOverview) {
        event.preventDefault();
        this.handleOverviewRemove(removeFromOverview);
      }
    }

    handleOverviewRemove(btn) {
      const di = parseInt(btn.dataset.deliveryIndex, 10);
      const key = btn.dataset.sectionKey;
      const variantId = btn.dataset.variantId;
      const list = this.selectionsFor(di, key);
      const idx = list.findIndex((s) => s.variantId === variantId);
      if (idx < 0) return;
      list.splice(idx, 1);
      // Reflect on the source card too, if rendered
      const card = this.root.querySelector(`[data-pb-card][data-variant-id="${variantId}"]`);
      if (card) {
        const sectionEl = card.closest("[data-pb-section]");
        if (sectionEl) {
          this.renderCard({ card, sectionEl, di, key, variantId });
        }
      }
      this.afterChange();
    }

    // Returns { ok: true } when the add is allowed, otherwise an object
    // describing why so the controller can surface a toast.
    // Inventory is counted across ALL deliveries — a stock-1 variant used
    // in delivery 1 cannot be picked again in delivery 2.
    checkLimit(ctx) {
      if (this.sectionFull(ctx)) return { ok: false, reason: "section_full" };
      let totalQty = 0;
      this.state.deliveries.forEach((d) => {
        Object.values(d.collections).forEach((col) => {
          col.selections.forEach((s) => {
            if (s.variantId === ctx.variantId) totalQty += s.qty;
          });
        });
      });
      if (totalQty + 1 > ctx.variantMax) {
        return { ok: false, reason: "inventory", max: ctx.variantMax };
      }
      return { ok: true };
    }

    notifyLimit(ctx, reason, max) {
      if (reason === "section_full") {
        this.showToast(this.i18n.toast_section_full);
        return;
      }
      if (reason === "inventory") {
        const key = max <= 0 ? "toast_inventory_zero" : "toast_inventory";
        this.showToast(this.fmt(key, { count: max, name: ctx.productTitle || "" }));
      }
    }

    handleAdd(btn) {
      const ctx = this.cardContext(btn);
      if (!ctx) return;
      const limit = this.checkLimit(ctx);
      if (!limit.ok) { this.notifyLimit(ctx, limit.reason, limit.max); return; }
      this.bumpQty(ctx, +1);
      this.renderCard(ctx);
      this.afterChange();
      this.maybeAdvanceCategory(ctx);
      this.maybeNudgeNextDelivery(ctx);
    }

    handleStep(btn, dir) {
      const ctx = this.cardContext(btn);
      if (!ctx) return;
      if (dir > 0) {
        const limit = this.checkLimit(ctx);
        if (!limit.ok) { this.notifyLimit(ctx, limit.reason, limit.max); return; }
      }
      this.bumpQty(ctx, dir);
      this.renderCard(ctx);
      this.afterChange();
      if (dir > 0) {
        this.maybeAdvanceCategory(ctx);
        this.maybeNudgeNextDelivery(ctx);
      }
    }

    cardContext(node) {
      const card = node.closest("[data-pb-card]");
      const sectionEl = node.closest("[data-pb-section]");
      if (!card || !sectionEl) return null;
      const di = parseInt(sectionEl.dataset.deliveryIndex, 10);
      const key = sectionEl.dataset.sectionKey;
      const cap = parseInt(sectionEl.dataset.cap, 10) || 0; // 0 = unlimited
      const variantMaxRaw = parseInt(card.dataset.variantMax, 10);
      return {
        card,
        sectionEl,
        di,
        key,
        cap,
        variantMax: Number.isFinite(variantMaxRaw) && variantMaxRaw > 0 ? variantMaxRaw : Infinity,
        productId: card.dataset.productId,
        variantId: card.dataset.variantId,
        sellingPlanId: card.dataset.sellingPlanId,
        variantPrice: parseInt(card.dataset.variantPrice, 10) || 0,
        productTitle: card.dataset.productTitle || "",
        imageUrl: card.dataset.cardImage || "",
        macros: {
          kcal: parseFloat(card.dataset.macroKcal) || 0,
          protein: parseFloat(card.dataset.macroProtein) || 0,
          carbs: parseFloat(card.dataset.macroCarbs) || 0,
          fat: parseFloat(card.dataset.macroFat) || 0,
        },
      };
    }

    selectionsFor(di, key) {
      return this.state.deliveries[di].collections[key].selections;
    }

    findSelection(di, key, variantId) {
      return this.selectionsFor(di, key).find((s) => s.variantId === variantId);
    }

    sectionCount(di, key) {
      return this.selectionsFor(di, key).reduce((sum, s) => sum + s.qty, 0);
    }

    sectionFull(ctx) {
      if (ctx.cap === 0) return false;
      return this.sectionCount(ctx.di, ctx.key) >= ctx.cap;
    }

    // ===== Add Ons =====

    addonsModel() {
      return window.PlanBuilderAddons || null;
    }

    addonCard(variantId) {
      if (variantId == null) return null;
      return this.root.querySelector(`[data-pb-addon-card][data-variant-id="${variantId}"]`);
    }

    addonMeta(card) {
      if (!card) return null;
      const variantMaxRaw = parseInt(card.dataset.variantMax, 10);
      return {
        productId: card.dataset.productId,
        variantId: card.dataset.variantId,
        sellingPlanId: card.dataset.sellingPlanId || null,
        variantPrice: parseInt(card.dataset.variantPrice, 10) || 0,
        productTitle: card.dataset.productTitle || "",
        imageUrl: card.dataset.cardImage || "",
        // Same sentinel rule as cardContext(): untracked stock is unlimited.
        variantMax: Number.isFinite(variantMaxRaw) && variantMaxRaw > 0 ? variantMaxRaw : Infinity,
        macros: {
          kcal: parseFloat(card.dataset.macroKcal) || 0,
          protein: parseFloat(card.dataset.macroProtein) || 0,
          carbs: parseFloat(card.dataset.macroCarbs) || 0,
          fat: parseFloat(card.dataset.macroFat) || 0,
        },
      };
    }

    // "Box 1" / "Box 2", or "Your box" when the plan has a single delivery.
    addonBoxLabel(box) {
      if (this.state.deliveries.length === 1) return this.i18n.addon_box_label_single || "";
      return this.fmt("addon_box_label", { number: box + 1 });
    }

    addonMaxPending({ variantId, fromBox, toBox }) {
      const M = this.addonsModel();
      const meta = this.addonMeta(this.addonCard(variantId));
      if (!M || !meta) return 0;
      return M.maxPending(this.state.deliveries, {
        variantId,
        variantMax: meta.variantMax,
        fromBox,
        toBox,
      });
    }

    // Absolute quantity for one Add On in one box (0 removes it), clamped to stock
    // and the per-box cap. Returns what was stored.
    setAddonQty(box, variantId, qty) {
      const M = this.addonsModel();
      const meta = this.addonMeta(this.addonCard(variantId));
      if (!M || !meta) return 0;
      const before = M.qtyIn(this.state.deliveries, box, variantId);
      const max = this.addonMaxPending({ variantId, fromBox: box, toBox: box });
      const stored = M.setQty(this.state.deliveries, box, meta, Math.min(qty, max));
      this.afterChange();
      if (stored > before) this.nudgeContinue();
      return stored;
    }

    // The modal's confirm: add (fromBox null), edit in place, or move between boxes.
    confirmAddon({ variantId, fromBox, toBox, qty }) {
      const M = this.addonsModel();
      const meta = this.addonMeta(this.addonCard(variantId));
      if (!M || !meta) return 0;
      const max = this.addonMaxPending({ variantId, fromBox, toBox });
      const stored = M.confirm(this.state.deliveries, {
        meta,
        fromBox,
        toBox,
        qty: Math.min(qty, max),
      });
      this.afterChange();
      if (fromBox == null && stored > 0) this.nudgeContinue();
      return stored;
    }

    // Restore path: one saved Add On line. Returns how many units were dropped.
    restoreAddon(box, variantId, qty) {
      const M = this.addonsModel();
      const meta = this.addonMeta(this.addonCard(variantId));
      if (!M || !meta || box >= this.state.deliveries.length) return qty;
      const max = M.maxPending(this.state.deliveries, {
        variantId,
        variantMax: meta.variantMax,
        fromBox: box,
        toBox: box,
      });
      const kept = M.setQty(this.state.deliveries, box, meta, Math.min(qty, max));
      return qty - kept;
    }

    bumpQty(ctx, delta) {
      const list = this.selectionsFor(ctx.di, ctx.key);
      let item = this.findSelection(ctx.di, ctx.key, ctx.variantId);
      if (!item && delta > 0) {
        item = {
          productId: ctx.productId,
          variantId: ctx.variantId,
          sellingPlanId: ctx.sellingPlanId || null,
          variantPrice: ctx.variantPrice,
          productTitle: ctx.productTitle,
          imageUrl: ctx.imageUrl,
          macros: ctx.macros,
          qty: 0,
        };
        list.push(item);
      }
      if (!item) return;
      item.qty += delta;
      if (item.qty <= 0) {
        const idx = list.indexOf(item);
        list.splice(idx, 1);
      }
    }

    renderCard(ctx) {
      const item = this.findSelection(ctx.di, ctx.key, ctx.variantId);
      const addBtn = ctx.card.querySelector("[data-pb-add]");
      const stepper = ctx.card.querySelector("[data-pb-stepper]");
      const qtyEl = ctx.card.querySelector("[data-pb-qty]");
      if (item) {
        addBtn.setAttribute("hidden", "");
        stepper.removeAttribute("hidden");
        qtyEl.textContent = String(item.qty);
      } else {
        addBtn.removeAttribute("hidden");
        stepper.setAttribute("hidden", "");
      }
    }

    // Only variantId -> qty is stored. Everything else on a selection (price, title,
    // image, selling plan) already lives on the card's data-* attributes and is rebuilt
    // by cardContext() on restore.
    selectionsPayload() {
      return {
        v: 1,
        plan: this.config.plan,
        days: this.config.daysPerWeek,
        meals: this.config.mealsPerDay,
        d: this.state.deliveries.map((delivery) => {
          const categories = {};
          Object.entries(delivery.collections).forEach(([key, col]) => {
            col.selections.forEach((s) => {
              categories[key] = categories[key] || {};
              categories[key][s.variantId] = s.qty;
            });
          });
          return categories;
        }),
      };
    }

    persistSelections() {
      const payload = this.selectionsPayload();
      const isEmpty = payload.d.every((categories) => Object.keys(categories).length === 0);
      // "" deletes the attribute, so emptying the basket cleans up after itself.
      this.queueSelectionsWrite(isEmpty ? "" : JSON.stringify(payload));
    }

    // A burst of + clicks must produce ONE request carrying the final state. Firing per
    // click races the requests, and an out-of-order response persists a stale grid --
    // the same bug already fixed for multi-select answers in the buying journey.
    queueSelectionsWrite(value) {
      clearTimeout(this._saveTimer);
      this._saveTimer = setTimeout(() => {
        this._saveTimer = null;
        // ALWAYS a string. An object round-trips fine through /cart.js but Shopify
        // coerces it to Ruby-hash text at checkout, which is invisible in dev.
        fetch(`${window.routes.cart_update_url}.js`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ attributes: { selections: value } }),
        }).catch((err) => console.error("[plan-builder] failed to save selections", err));
      }, 400);
    }

    parseSavedSelections() {
      const raw = this.root.dataset.savedSelections;
      if (!raw) return this.cartDerivedSelections();
      try {
        return JSON.parse(raw);
      } catch (e) {
        console.error("[plan-builder] failed to parse saved selections", e);
        return this.cartDerivedSelections();
      }
    }

    // finishCheckout() clears the `selections` attribute the moment the plan reaches
    // the cart, so "Edit your meal plan" on the cart review page used to land on an
    // empty builder and make the customer pick every meal again to change one. The
    // bundle's line items are the same plan in durable form, so rebuild the payload
    // from those and hand it to the ordinary restore path.
    //
    // The attribute still wins when present: it is the live working state, and it only
    // exists while the plan has NOT been added to the cart.
    cartDerivedSelections() {
      const node = this.root.querySelector("[data-pb-cart-plan]");
      if (!node) return null;

      let cart;
      try {
        cart = JSON.parse(node.textContent.trim());
      } catch (e) {
        console.error("[plan-builder] failed to parse cart plan", e);
        return null;
      }
      if (!cart || !Array.isArray(cart.lines) || cart.lines.length === 0) return null;

      // These lines belong to a plan; make sure it is THIS one. The parent bundle
      // variant is that identity -- unlike cart.attributes.plan, which the deep-link
      // reconciler repoints at whatever plan page the customer last opened, leaving it
      // claiming plan B while the cart still holds plan A's boxes.
      const ours = new Set(
        this.state.deliveries.map((d) => String(d.bundle && d.bundle.variantId)),
      );
      const bundles = Array.isArray(cart.bundles) ? cart.bundles : [];
      if (!bundles.some((id) => ours.has(String(id)))) return null;

      // Deliberately not clamped to the current delivery count: restoreSelections()
      // reports deliveries that no longer exist as dropped meals, and that stays true
      // whether the payload came from the attribute or from the cart.
      const d = [];
      cart.lines.forEach((line) => {
        const di = parseInt(line.d, 10);
        const qty = parseInt(line.q, 10);
        const variantId = String(line.v == null ? "" : line.v);
        if (!Number.isFinite(di) || di < 0 || !variantId || !(qty > 0)) return;
        while (d.length <= di) d.push({});
        // "a" marks an Add On line (see data-pb-cart-plan in sections/plan-builder.liquid).
        const key = line.a ? "extras" : ANY_SECTION;
        const bucket = (d[di][key] = d[di][key] || {});
        bucket[variantId] = (bucket[variantId] || 0) + qty;
      });
      if (d.length === 0) return null;

      // The bundle check above already proved the plan; naming it here satisfies
      // restoreSelections()'s own plan guard without re-deciding the question.
      return { v: 1, plan: this.config.plan, d };
    }

    // Saved selections are a claim about the past; the world moves between visits.
    // Every unit is replayed through the SAME checkLimit + bumpQty a real click uses,
    // so section caps, cross-delivery inventory and sold-out meals are enforced
    // identically. Anything that no longer fits is dropped, and the customer is told.
    restoreSelections() {
      const saved = this.parseSavedSelections();
      if (!saved || !Array.isArray(saved.d)) return;

      // Selections from one plan's collections are meaningless in another's.
      if (saved.plan !== this.config.plan) return;

      let dropped = 0;
      // Meals are sold as plan-tier variants, so a card's variant id changes with
      // days and meals per day. A plan saved under another config (or before tiers
      // existed) holds a sibling variant of the same dish: the card lists every
      // variant id it stands for, and the selection takes on whatever that card
      // sells now. Without this a quiz edit would empty the whole basket.
      let remapped = false;
      const cardFor = (scope, variantId) => {
        const exact = this.root.querySelector(`${scope} [data-pb-card][data-variant-id="${variantId}"]`);
        if (exact) return exact;
        const sibling = this.root.querySelector(`${scope} [data-pb-card][data-variant-ids~="${variantId}"]`);
        if (sibling) remapped = true;
        return sibling;
      };
      const countOf = (categories) =>
        Object.values(categories || {}).reduce(
          (sum, byVariant) => sum + Object.values(byVariant).reduce((a, b) => a + b, 0),
          0
        );

      saved.d.forEach((categories, di) => {
        // Fewer deliveries than when they saved (days went 5 -> 3).
        if (di >= this.state.deliveries.length) {
          dropped += countOf(categories);
          return;
        }

        Object.entries(categories || {}).forEach(([key, byVariant]) => {
          Object.entries(byVariant).forEach(([variantId, qty]) => {
            // Add Ons live in the shared panel, not a per-delivery section, so the
            // section lookup below can never find them. Carts from before the "a"
            // flag arrive as ANY_SECTION: send those here too when no section
            // renders the variant but an Add On card does.
            const inSection = cardFor(`[data-pb-section][data-delivery-index="${di}"]`, variantId);
            if (key === "extras" || (key === ANY_SECTION && !inSection && this.addonCard(variantId))) {
              dropped += this.restoreAddon(di, variantId, qty);
              return;
            }

            // With ANY_SECTION the first section rendering this variant wins, and
            // cardContext() reads the real key off that section — so the section cap
            // checked below is the one the meal actually belongs to.
            const sectionSelector =
              key === ANY_SECTION
                ? `[data-pb-section][data-delivery-index="${di}"]`
                : `[data-pb-section][data-delivery-index="${di}"][data-section-key="${key}"]`;
            const card = cardFor(sectionSelector, variantId);
            // No card means the meal is gone: unavailable, or filtered out by the
            // customer's allergen list. Either way it cannot be restored.
            const addBtn = card?.querySelector("[data-pb-add]");
            const ctx = addBtn ? this.cardContext(addBtn) : null;
            if (!ctx) {
              dropped += qty;
              return;
            }

            for (let i = 0; i < qty; i += 1) {
              if (!this.checkLimit(ctx).ok) {
                dropped += qty - i;
                break;
              }
              this.bumpQty(ctx, +1);
            }
            this.renderCard(ctx);
          });
        });
      });

      this.refreshUi();

      if (dropped > 0) {
        this.showToast(this.i18n.toast_restore_trimmed);
      }
      // Save the basket back when it changed shape: trimmed (otherwise the stale
      // payload is re-trimmed and re-toasted on every page load) or re-keyed onto
      // this plan's tier variants. A re-key only REWRITES an attribute that already
      // exists: when the payload came from the cart lines the plan is already in the
      // cart, the attribute must stay absent (it would ride into checkout and win
      // over the real lines next visit), and the sibling lookup re-keys every load.
      const hasAttribute = Boolean(this.root.dataset.savedSelections);
      if (dropped > 0 || (remapped && hasAttribute)) this.persistSelections();
    }

    afterChange() {
      this.refreshUi();
      this.persistSelections();
    }

    refreshUi() {
      this.expose();
      this.emit();
      this.updateFooter();
      this.updateOverview();
      this.updateCheckoutCtaStates();
      this.updateDeliveryTabs();
      this.updateSectionHeader();
      this.updateCategoryTabs();
      this.updateSectionCounts();
      if (this.activeFilters.size > 0) this.applyFilters();
    }

    toggleFilter() {
      const panel = this.root.querySelector("[data-pb-filter-panel]");
      if (!panel) return;
      if (panel.hasAttribute("hidden")) this.openFilter();
      else this.closeFilter();
    }

    openFilter() {
      const toggle = this.root.querySelector("[data-pb-filter-toggle]");
      const panel = this.root.querySelector("[data-pb-filter-panel]");
      if (!toggle || !panel) return;
      panel.removeAttribute("hidden");
      toggle.setAttribute("aria-expanded", "true");
    }

    closeFilter() {
      const toggle = this.root.querySelector("[data-pb-filter-toggle]");
      const panel = this.root.querySelector("[data-pb-filter-panel]");
      if (!toggle || !panel || panel.hasAttribute("hidden")) return;
      panel.setAttribute("hidden", "");
      toggle.setAttribute("aria-expanded", "false");
    }

    clearFilters() {
      this.root.querySelectorAll("[data-pb-filter-key]").forEach((input) => {
        input.checked = false;
      });
      this.applyFilters();
    }

    // Visual-only filter: hide cards whose data-flags don't contain EVERY
    // active filter key (AND mode). Caps, counts, sidebar, and progress are
    // untouched — only the visible card list changes.
    applyFilters() {
      this.activeFilters = new Set(
        Array.from(this.root.querySelectorAll("[data-pb-filter-key]:checked"))
          .map((input) => input.dataset.pbFilterKey)
      );

      this.root.querySelectorAll("[data-pb-card]").forEach((card) => {
        const flags = (card.dataset.flags || "").split(/\s+/).filter(Boolean);
        const matches = Array.from(this.activeFilters).every((key) => flags.includes(key));
        card.classList.toggle("is-filtered-out", !matches);
      });

      this.updateFilterAffordances();
      this.updateSectionEmptyStates();
    }

    updateFilterAffordances() {
      const count = this.activeFilters.size;
      const badge = this.root.querySelector("[data-pb-filter-count]");
      if (badge) {
        badge.textContent = count > 0 ? String(count) : "";
        if (count > 0) badge.removeAttribute("hidden");
        else badge.setAttribute("hidden", "");
      }
      // Scoped to the panel: the grid's empty-state clear button shares the
      // same hook but is created on demand, so it must not be toggled here.
      const clearBtn = this.root.querySelector("[data-pb-filter-panel] [data-pb-filter-clear]");
      if (clearBtn) {
        if (count > 0) clearBtn.removeAttribute("hidden");
        else clearBtn.setAttribute("hidden", "");
      }
    }

    updateSectionEmptyStates() {
      this.root.querySelectorAll("[data-pb-section]").forEach((sectionEl) => {
        // Only inject the filter empty-state row into sections the user can
        // currently see — skip closed (inactive-tab) sections and hidden
        // (inactive) deliveries.
        const sectionHidden = !sectionEl.open;
        const deliveryEl = sectionEl.closest("[data-pb-delivery]");
        const deliveryHidden = deliveryEl && deliveryEl.hasAttribute("hidden");
        const body = sectionEl.querySelector(".plan-builder__section-body");
        if (!body) return;
        let emptyRow = body.querySelector("[data-pb-filter-empty]");
        const grid = body.querySelector(".plan-builder__grid");
        const shouldShow = !sectionHidden && !deliveryHidden && this.activeFilters.size > 0 && grid &&
          !grid.querySelector("[data-pb-card]:not(.is-filtered-out)");
        if (shouldShow) {
          if (!emptyRow) {
            emptyRow = document.createElement("p");
            // Site sub copy type - same classes as the Liquid empty state in
            // snippets/plan-builder-section.liquid (not text-b3's Montserrat).
            emptyRow.className = "plan-builder__empty font-display font-medium text-[14px] min-[990px]:text-[16px] leading-normal tracking-[-0.14px] min-[990px]:tracking-[-0.18px] text-dark-grey";
            emptyRow.dataset.pbFilterEmpty = "1";
            emptyRow.innerHTML = `${this.i18n.no_filter_match} <button type="button" class="plan-builder__filter-empty-clear" data-pb-filter-clear>${this.i18n.clear_filters}</button>`;
            body.appendChild(emptyRow);
          }
        } else if (emptyRow) {
          emptyRow.remove();
        }
      });
    }

    money(cents) {
      if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
        return window.Shopify.formatMoney(cents);
      }
      return `£${(cents / 100).toFixed(2)}`;
    }

    updateOverview() {
      this.updateOverviewSlots();
      this.updateOverviewAddons();
      this.updateScrollbars();
      this.updateOverviewPricing();
      this.updateOverviewMacros();
      this.updateFooterTotal();
    }

    // Daily Average Macros Total: every selected item's macro metafields
    // (extras included), summed across BOTH deliveries, divided by the plan's
    // total day count — so the figure reads "per day" however the days split
    // between boxes. Items without macro data contribute zero.
    updateOverviewMacros() {
      const totals = { kcal: 0, protein: 0, carbs: 0, fat: 0 };
      let days = 0;
      this.state.deliveries.forEach((d) => {
        days += d.dayCount || 0;
        Object.values(d.collections).forEach((col) => {
          col.selections.forEach((s) => {
            const m = s.macros || {};
            totals.kcal += (m.kcal || 0) * s.qty;
            totals.protein += (m.protein || 0) * s.qty;
            totals.carbs += (m.carbs || 0) * s.qty;
            totals.fat += (m.fat || 0) * s.qty;
          });
        });
      });
      const divisor = days || 1;
      // querySelectorAll: mobile renders the overview twice (inline under the
      // accordions + inside the sticky sheet), and both have to stay live.
      this.root.querySelectorAll("[data-pb-macro]").forEach((el) => {
        const key = el.dataset.pbMacro;
        const value = Math.round((totals[key] || 0) / divisor);
        el.textContent = key === "kcal" ? String(value) : `${value}g`;
      });
    }

  
    updateScrollbars() {
      this.root.querySelectorAll("[data-pb-overview-track]").forEach((track) => {
        // Scoped to the delivery block, not track.parentElement: the mobile
        // card wrapper sits between the two, and the pill scrollbar is outside
        // it (a sibling of the card, not of the track).
        const deliveryEl = track.closest("[data-pb-overview-delivery]") || track.parentElement;
        const bar = deliveryEl.querySelector("[data-pb-scrollbar]");
        const thumb = bar && bar.querySelector("[data-pb-scrollbar-thumb]");
        if (!bar || !thumb) return;
        if (!track._sbBound) {
          track.addEventListener("scroll", () => this.layoutScrollbar(track, bar, thumb), { passive: true });
          this._bindScrollbarDrag(track, bar, thumb);
          this._bindTrackDrag(track);
          track._sbBound = true;
        }
        this.layoutScrollbar(track, bar, thumb);
      });
    }

    layoutScrollbar(track, bar, thumb) {
      const ratio = track.scrollWidth > 0 ? track.clientWidth / track.scrollWidth : 1;
      // Drives the grab cursor: no overflow, nothing to drag.
      track.classList.toggle("is-scrollable", ratio < 0.999);
      if (ratio >= 0.999) { bar.setAttribute("hidden", ""); return; }
      bar.removeAttribute("hidden");
      const thumbPct = Math.max(ratio * 100, 12);
      thumb.style.width = `${thumbPct}%`;
      const maxScroll = track.scrollWidth - track.clientWidth;
      const p = maxScroll > 0 ? track.scrollLeft / maxScroll : 0;
      thumb.style.left = `${p * (100 - thumbPct)}%`;
    }

    _bindScrollbarDrag(track, bar, thumb) {
      let startX = 0, startScroll = 0, dragging = false;
      const move = (e) => {
        if (!dragging) return;
        const range = bar.clientWidth - thumb.clientWidth;
        const maxScroll = track.scrollWidth - track.clientWidth;
        if (range <= 0) return;
        track.scrollLeft = startScroll + ((e.clientX - startX) / range) * maxScroll;
      };
      const up = () => {
        dragging = false;
        document.removeEventListener("pointermove", move);
        document.removeEventListener("pointerup", up);
      };
      thumb.addEventListener("pointerdown", (e) => {
        e.preventDefault();
        dragging = true;
        startX = e.clientX;
        startScroll = track.scrollLeft;
        document.addEventListener("pointermove", move);
        document.addEventListener("pointerup", up);
      });
      bar.addEventListener("pointerdown", (e) => {
        if (e.target !== bar) return; // ignore clicks on the thumb itself
        const rect = bar.getBoundingClientRect();
        const maxScroll = track.scrollWidth - track.clientWidth;
        track.scrollLeft = ((e.clientX - rect.left) / rect.width) * maxScroll;
      });
    }

    // Grab-and-pan the slot strip itself, alongside the scrollbar thumb.
    // Touch pointers are ignored on purpose: the track is `overflow-x: auto`,
    // so mobile already pans natively and hijacking it here would fight the
    // browser's own momentum scrolling.
    _bindTrackDrag(track) {
      const DRAG_THRESHOLD = 5; // px before a press counts as a drag, not a click
      let startX = 0, startScroll = 0, dragging = false, moved = false;

      const move = (e) => {
        if (!dragging) return;
        const dx = e.clientX - startX;
        if (!moved && Math.abs(dx) > DRAG_THRESHOLD) {
          moved = true;
          track.classList.add("is-dragging");
        }
        if (moved) track.scrollLeft = startScroll - dx;
      };

      const up = () => {
        if (!dragging) return;
        dragging = false;
        track.classList.remove("is-dragging");
        document.removeEventListener("pointermove", move);
        document.removeEventListener("pointerup", up);
        document.removeEventListener("pointercancel", up);
        // A drag that ends over the remove button would otherwise fire its
        // click. Swallow that one click during capture, before it reaches
        // the delegated handler on the section root.
        if (moved) {
          track.addEventListener("click", (e) => {
            e.stopPropagation();
            e.preventDefault();
          }, { capture: true, once: true });
        }
      };

      track.addEventListener("pointerdown", (e) => {
        if (e.pointerType === "touch") return;
        if (e.button != null && e.button !== 0) return; // primary button only
        if (track.scrollWidth <= track.clientWidth) return;
        dragging = true;
        moved = false;
        startX = e.clientX;
        startScroll = track.scrollLeft;
        document.addEventListener("pointermove", move);
        document.addEventListener("pointerup", up);
        document.addEventListener("pointercancel", up);
      });
    }

    // Cutlery (fork+knife) markup for empty slots — cloned from the Liquid
    // template so the SVG path lives in one place.
    _cutlerySvg() {
      if (this._cutlery == null) {
        const tpl = this.root.querySelector("[data-pb-cutlery]");
        this._cutlery = tpl ? tpl.innerHTML.trim() : "";
      }
      return this._cutlery;
    }

    updateOverviewSlots() {
      this.state.deliveries.forEach((delivery, di) => {
        // querySelectorAll, not querySelector: mobile renders the overview
        // twice (inline under the accordions + inside the sticky sheet), so
        // every delivery block has a twin that needs the same slots.
        this.root.querySelectorAll(`[data-pb-overview-delivery="${di}"]`).forEach((deliveryEl) => {
        const grid = deliveryEl.querySelector("[data-pb-overview-slots]");
        if (!grid) return;
        // The strip is the box's meals only. Add Ons have their own list below it
        // (updateOverviewAddons). Extra slots are still appended on the fly in case
        // restored selections ever exceed the Liquid-rendered N.
        const sectionKeys = ["breakfast", "meals", "snacks"];
        const filled = [];
        sectionKeys.forEach((key) => {
          delivery.collections[key].selections.forEach((s) => filled.push({ ...s, sectionKey: key }));
        });
        let slots = Array.from(grid.querySelectorAll(".plan-builder__overview-slot"));
        while (slots.length < filled.length) {
          const newSlot = document.createElement("div");
          newSlot.className = "plan-builder__overview-slot";
          newSlot.dataset.slotIndex = String(slots.length);
          grid.appendChild(newSlot);
          slots.push(newSlot);
        }

        // Once every required portion is chosen, leftover cutlery placeholders
        // only suggest more picking is needed — hide (not remove) them so they
        // reappear if a selection is removed. qty > 1 selections mean the box
        // can be full with fewer occupied slots than the Liquid-rendered N.
        const total = parseInt(deliveryEl.dataset.totalDishes, 10) || slots.length;
        const boxFull = filled.reduce((sum, s) => sum + s.qty, 0) >= total;

        let portionsFilled = 0;
        slots.forEach((slot, idx) => {
          const sel = filled[idx];
          slot.innerHTML = "";
          if (sel) {
            slot.hidden = false;
            const removeBtn = document.createElement("button");
            removeBtn.type = "button";
            removeBtn.className = "plan-builder__overview-slot-remove";
            removeBtn.setAttribute("aria-label", this.fmt("card_remove_aria", { title: sel.productTitle || "" }));
            removeBtn.dataset.pbOverviewRemove = "1";
            removeBtn.dataset.deliveryIndex = String(di);
            removeBtn.dataset.sectionKey = sel.sectionKey;
            removeBtn.dataset.variantId = sel.variantId;
            removeBtn.textContent = "×";
            slot.appendChild(removeBtn);

            if (sel.imageUrl) {
              const img = document.createElement("img");
              img.src = sel.imageUrl;
              img.alt = sel.productTitle || "";
              img.loading = "lazy";
              slot.appendChild(img);
            } else {
              // No featured image — render a placeholder thumbnail rather than
              // falling through to the empty-slot icon (which would hide the
              // fact that the slot is selected).
              const placeholder = document.createElement("span");
              placeholder.className = "plan-builder__overview-slot-placeholder";
              placeholder.setAttribute("aria-hidden", "true");
              placeholder.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="6"/></svg>';
              slot.appendChild(placeholder);
            }

            const qtyBadge = document.createElement("span");
            qtyBadge.className = "plan-builder__overview-slot-qty";
            qtyBadge.textContent = `x${sel.qty}`;
            slot.appendChild(qtyBadge);

            portionsFilled += sel.qty;
          } else {
            slot.hidden = boxFull;
            const empty = document.createElement("span");
            empty.className = "plan-builder__overview-slot-empty";
            empty.setAttribute("aria-hidden", "true");
            empty.innerHTML = this._cutlerySvg();
            slot.appendChild(empty);
          }
        });

        const instruction = deliveryEl.querySelector("[data-pb-overview-instruction]");
        if (instruction) {
          // Single delivery drops the "first"/"second": "for your box", not "for your
          // first box" — a separate string rather than a blank box_word, which
          // would leave a double space. Mirrors snippets/plan-builder-overview.liquid.
          const boxWord = this.i18n[di === 0 ? "box_word_first" : "box_word_second"] || "";
          const selectCopy = this.state.deliveries.length === 1
            ? this.fmt("select_total_meals_single", { count: total })
            : this.fmt("select_total_meals", { count: total, box: boxWord });
          instruction.innerHTML = portionsFilled === 0
            ? selectCopy
            : this.fmt("progress_meals", { selected: portionsFilled, total });
        }
        });
      });
    }

    // ADD ONS list under each box. Rebuilt from state on every change, in both
    // overview copies (mobile renders the overview twice).
    updateOverviewAddons() {
      const M = this.addonsModel();
      if (!M) return;
      this.state.deliveries.forEach((_, box) => {
        const lines = M.lines(this.state.deliveries, box);
        this.root.querySelectorAll(`[data-pb-overview-addons="${box}"]`).forEach((list) => {
          list.textContent = "";
          if (!lines.length) {
            const empty = document.createElement("li");
            empty.className = "plan-builder__overview-addons-empty";
            empty.textContent = this.i18n.addon_no_addons || "";
            list.appendChild(empty);
            return;
          }
          lines.forEach((line) => list.appendChild(this.overviewAddonRow(line)));
        });
      });
    }

    overviewAddonRow(line) {
      const boxLabel = this.addonBoxLabel(line.box);
      const row = document.createElement("li");
      row.className = "plan-builder__overview-addon";

      let thumb;
      if (line.imageUrl) {
        thumb = document.createElement("img");
        thumb.src = line.imageUrl;
        thumb.alt = "";
        thumb.loading = "lazy";
      } else {
        thumb = document.createElement("span");
        thumb.setAttribute("aria-hidden", "true");
      }
      thumb.className = "plan-builder__overview-addon-thumb";

      // The name opens the modal in edit mode (component-plan-builder-addons.js).
      const body = document.createElement("button");
      body.type = "button";
      body.className = "plan-builder__overview-addon-body";
      body.dataset.pbAddonEdit = "1";
      body.dataset.box = String(line.box);
      body.dataset.variantId = String(line.variantId);
      const name = document.createElement("strong");
      name.className = "plan-builder__overview-addon-name";
      name.textContent = `${line.productTitle} ×${line.qty}`;
      const boxEl = document.createElement("small");
      boxEl.className = "plan-builder__overview-addon-box";
      boxEl.textContent = boxLabel;
      body.append(name, boxEl);

      const price = document.createElement("span");
      price.className = "plan-builder__overview-addon-price";
      price.textContent = `+${this.money(line.lineCents)}`;

      const remove = document.createElement("button");
      remove.type = "button";
      remove.className = "plan-builder__overview-addon-remove";
      remove.dataset.pbAddonRemove = "1";
      remove.dataset.box = String(line.box);
      remove.dataset.variantId = String(line.variantId);
      remove.setAttribute("aria-label", this.fmt("addon_remove_aria", { title: line.productTitle, box: boxLabel }));
      remove.textContent = "×";

      row.append(thumb, body, price, remove);
      return row;
    }

    sumSelections(key) {
      let cents = 0;
      this.state.deliveries.forEach((d) => {
        d.collections[key].selections.forEach((s) => {
          cents += (s.variantPrice || 0) * s.qty;
        });
      });
      return cents;
    }

    // Plan (the whole week's price, see planPriceCents) + Add ons = Total. The
    // introductory offer comes off the whole Total, as the cart applies it
    // across every line, Add Ons included; flooring matches the cart's figure.
    // The Continue CTAs carry dueCents, so the price on the button is what the
    // customer is about to pay.
    planTotals() {
      const M = this.addonsModel();
      const planCents = this.planPriceCents();
      const addonsCents = M ? M.totalCents(this.state.deliveries) : 0;
      const addonsCount = M ? M.totalUnits(this.state.deliveries) : 0;
      const totalCents = planCents + addonsCents;
      const introPercent = this.config?.introOffer?.percent || 0;
      const introCents = Math.floor((totalCents * introPercent) / 100);
      return { planCents, addonsCents, addonsCount, totalCents, introCents, dueCents: totalCents - introCents };
    }

    updateOverviewPricing() {
      const { planCents, addonsCents, addonsCount, totalCents, introCents, dueCents } = this.planTotals();
      // querySelectorAll: mobile renders the overview twice (inline under the
      // accordions + inside the sticky sheet), and both have to stay live.
      const set = (selector, value) => {
        this.root.querySelectorAll(selector).forEach((el) => { el.textContent = value; });
      };
      set("[data-pb-plan-price]", this.money(planCents));
      set("[data-pb-addons-total]", this.money(addonsCents));
      // The label carries how many Add Ons the figure covers ("Add ons ×3").
      // A missing string leaves the server-rendered label alone.
      const addonsLabel = this.fmt("row_extras_count", { count: addonsCount });
      if (addonsLabel) set("[data-pb-addons-label]", addonsLabel);
      this.root.querySelectorAll("[data-pb-addons-row]").forEach((row) => {
        row.hidden = addonsCents === 0;
      });
      set("[data-pb-total]", this.money(dueCents));
      set("[data-pb-continue-price]", this.money(dueCents));
      // Intro offer: these hooks only render while there is one.
      set("[data-pb-intro-saving]", `-${this.money(introCents)}`);
      set("[data-pb-total-original]", this.money(totalCents));
      if (introCents > 0) {
        const note = this.fmt("intro_offer_note", { price: this.money(totalCents) });
        if (note) set("[data-pb-intro-note]", note);
        const campaignNote = this.fmt("campaign_offer_note", { price: this.money(totalCents) });
        if (campaignNote) set("[data-pb-campaign-note]", campaignNote);
      }
    }

    // The plan price: starts as the "starting from" estimate and follows the
    // customer's picks, each filled slot at its dish's real price, until a full
    // plan is exactly what checkout charges. The maths lives in
    // plan-builder-pricing-model.js; this only gathers each section's key, cap,
    // cards and selections.
    planPriceCents() {
      const P = window.PlanBuilderPricing;
      if (!P) return 0;
      const sections = [];
      this.root.querySelectorAll("[data-pb-section]").forEach((sectionEl) => {
        const di = parseInt(sectionEl.dataset.deliveryIndex, 10);
        const key = sectionEl.dataset.sectionKey;
        const collection = this.state.deliveries[di] && this.state.deliveries[di].collections[key];
        if (!collection) return;
        const cards = this.sectionCards(sectionEl);
        sections.push({
          key,
          cap: parseInt(sectionEl.dataset.cap, 10) || 0,
          slot: P.slotFromCards(cards),
          uplift: P.upliftCents(cards),
          selections: collection.selections,
        });
      });
      return P.planCents(sections);
    }

    // A section's cards as the pricing model reads them: the price the card
    // sells at (the tier price under a plan tier) and its Standard price. Sold-out
    // cards are marked so they don't vote on the slot price or the uplift.
    sectionCards(sectionEl) {
      return Array.from(sectionEl.querySelectorAll("[data-pb-card]"), (card) => ({
        price: parseInt(card.dataset.variantPrice, 10) || 0,
        standardPrice: parseInt(card.dataset.standardPrice, 10) || 0,
        soldOut: card.dataset.variantMax === "0",
      }));
    }

    updateFooterTotal() {
      const { totalCents, dueCents } = this.planTotals();
      // The desktop "Weekly Total:" cell - the same Total as the overview.
      this.root.querySelectorAll("[data-pb-weekly-total]").forEach((el) => {
        el.textContent = this.money(dueCents);
      });
      this.root.querySelectorAll("[data-pb-weekly-original]").forEach((el) => {
        el.textContent = this.money(totalCents);
      });
    }

    activeDeliveryEl() {
      return this.root.querySelector(`[data-pb-delivery][data-delivery-index="${this.state.activeDeliveryIndex}"]`);
    }

    // ===== Delivery pills + category tabs =====
    // A delivery pill switches which delivery's sections are visible; a
    // category tab switches which section (breakfast/meals/snacks/extras)
    // shows within the active delivery. Checkout readiness is still validated
    // across ALL deliveries (isReadyForCheckout), so the footer/overview
    // "Continue" remains the single checkout action.

    setActiveDelivery(idx) {
      if (!Number.isFinite(idx)) return;
      if (idx < 0 || idx >= this.state.deliveries.length) return;
      if (idx === this.state.activeDeliveryIndex) return;
      this.state.activeDeliveryIndex = idx;
      // Switching delivery starts that delivery from its first category tab,
      // rather than keeping whatever tab was open in the previous delivery.
      this.state.activeCategory = this.categoryOrder()[0];
      this.applyActiveDelivery();
      this.applyActiveCategory();
      this.updateSectionHeader();
      this.updateCategoryTabs();
      this.updateFooter();
      this.expose();
      this.emit();
    }

    applyActiveDelivery() {
      this.root.querySelectorAll("[data-pb-delivery]").forEach((el) => {
        const i = parseInt(el.dataset.deliveryIndex, 10);
        if (i === this.state.activeDeliveryIndex) el.removeAttribute("hidden");
        else el.setAttribute("hidden", "");
      });
      this.root.querySelectorAll("[data-pb-delivery-tab]").forEach((tab) => {
        const active = parseInt(tab.dataset.deliveryIndex, 10) === this.state.activeDeliveryIndex;
        tab.classList.toggle("is-active", active);
        tab.setAttribute("aria-selected", active ? "true" : "false");
      });
    }

    setActiveCategory(key, opts) {
      if (!key || key === this.state.activeCategory) return;
      this.state.activeCategory = key;
      this.applyActiveCategory(opts);
      this.updateSectionHeader();
      if (this.activeFilters.size > 0) this.applyFilters();
    }

    // Order of category tabs within a delivery (only the ones that apply).
    categoryOrder() {
      const order = [];
      if (this.state.hasBreakfast) order.push("breakfast");
      order.push("meals");
      if (this.state.hasSnacks) order.push("snacks");
      if (this.state.hasExtras) order.push("extras");
      return order;
    }

    // After an add that fills the active category to its cap, advance to the
    // next category tab — but only the first time that category is completed,
    // so the user can freely click a tab back to edit without being bounced
    // forward again. Extras (cap 0) never auto-advances.
    maybeAdvanceCategory(ctx) {
      if (!ctx || ctx.cap <= 0) return;
      if (ctx.key !== this.state.activeCategory) return;
      if (this.sectionCount(ctx.di, ctx.key) < ctx.cap) return;
      const seen = `${ctx.di}:${ctx.key}`;
      if (this._advancedCategories.has(seen)) return;
      this._advancedCategories.add(seen);
      const order = this.categoryOrder();
      const next = order[order.indexOf(ctx.key) + 1];
      if (next) {
        this.setActiveCategory(next);
        this.announceCategoryChange(next);
      }
    }

    // ===== Attention nudges =====
    // The tabs live above the grid and are usually scrolled off-screen by the
    // time a category fills up, so an auto-advance is easy to miss. Each nudge
    // is scroll (only if needed) + haptic + a short shake/bounce animation.

    _prefersReducedMotion() {
      return window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    }

    hapticBuzz() {
      if (this._prefersReducedMotion()) return;
      if (navigator.vibrate) navigator.vibrate(40);
    }

    // Re-adds the animation class so repeat nudges replay the animation.
    nudge(el, className) {
      if (!el || this._prefersReducedMotion()) return;
      el.classList.remove(className);
      void el.offsetWidth;
      el.classList.add(className);
      el.addEventListener("animationend", () => el.classList.remove(className), { once: true });
    }

    // Returns true when a scroll was needed so callers can hold the animation
    // until the element lands on screen. The scroll stops with the delivery
    // pills at the top of the viewport — not the page top — so the product
    // cards below the tabs stay in view.
    scrollToIfHidden(el) {
      if (!el) return false;
      const r = el.getBoundingClientRect();
      if (r.top >= 0 && r.bottom <= window.innerHeight) return false;
      const anchor = this.root.querySelector("[data-pb-delivery-tabs]") || el;
      const top = window.scrollY + anchor.getBoundingClientRect().top - 12;
      window.scrollTo({
        top: Math.max(top, 0),
        behavior: this._prefersReducedMotion() ? "auto" : "smooth",
      });
      return true;
    }

    nudgeAfterScroll(anchor, target, className) {
      const scrolled = this.scrollToIfHidden(anchor);
      this.hapticBuzz();
      setTimeout(() => this.nudge(target, className), scrolled ? 400 : 0);
    }

    // Add Ons are the last, optional step, so an Add On added to a plan that is
    // already complete is the customer finishing. Nothing else changes on
    // screen at that point, so bring Continue into view and shake it.
    nudgeContinue() {
      if (!this.isReadyForCheckout()) return;
      // Desktop: Continue is the last thing in the sticky overview, which
      // scrolls on its own once the Add Ons list makes it taller than the
      // viewport. (Mobile's Continue lives in the fixed bar, always on screen.)
      let scrolled = false;
      this.root.querySelectorAll("[data-pb-overview-continue]").forEach((cta) => {
        const panel = cta.closest(".plan-builder__overview");
        const r = cta.getBoundingClientRect();
        if (!panel || r.height === 0) return;
        const p = panel.getBoundingClientRect();
        if (r.top >= p.top && r.bottom <= p.bottom) return;
        panel.scrollTo({
          top: panel.scrollHeight,
          behavior: this._prefersReducedMotion() ? "auto" : "smooth",
        });
        scrolled = true;
      });
      this.hapticBuzz();
      // Wait out the scroll, and give the Add On modal a beat to close first.
      const ctas = this.root.querySelectorAll("[data-pb-footer-continue], [data-pb-overview-continue]");
      setTimeout(() => ctas.forEach((cta) => this.nudge(cta, "is-shaking")), scrolled ? 400 : 250);
    }

    announceCategoryChange(key) {
      const tabs = this.root.querySelector("[data-pb-category-tabs]");
      const tab = this.root.querySelector(`[data-pb-category-tab][data-section-key="${key}"]`);
      this.nudgeAfterScroll(tabs, tab, "is-shaking");
    }

    // The moment a delivery's required portions are all picked, move the user
    // to the next delivery that still needs selections — once per delivery, so
    // hopping back to a completed tab to edit doesn't keep bouncing them
    // forward. The shake on the newly active tab shows what just happened.
    maybeNudgeNextDelivery(ctx) {
      if (!ctx || !this.deliveryComplete(ctx.di)) return;
      const nextDi = this.state.deliveries.findIndex(
        (_, di) => di !== ctx.di && !this.deliveryComplete(di)
      );
      if (nextDi < 0) return;
      if (this._nudgedDeliveries.has(nextDi)) return;
      this._nudgedDeliveries.add(nextDi);
      this.setActiveDelivery(nextDi);
      const tab = this.root.querySelector(`[data-pb-delivery-tab][data-delivery-index="${nextDi}"]`);
      const scrolled = this.scrollToDeliveryStart(nextDi);
      this.hapticBuzz();
      setTimeout(() => this.nudge(tab, "is-shaking"), scrolled ? 400 : 0);
    }

    // The delivery tabs are sticky, so they never read as off-screen and
    // scrollToIfHidden would leave the customer down by the footer of the
    // delivery they just finished. Instead, bring the new delivery's first
    // card up to sit just under the pinned tab rows. Returns true if it moved.
    scrollToDeliveryStart(di) {
      const deliveryEl = this.root.querySelector(`[data-pb-delivery][data-delivery-index="${di}"]`);
      if (!deliveryEl) return false;
      let stickyBottom = 0;
      this.root.querySelectorAll("[data-pb-delivery-tabs], [data-pb-category-tabs]").forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.height > 0) stickyBottom = Math.max(stickyBottom, r.bottom);
      });
      const top = deliveryEl.getBoundingClientRect().top;
      if (top >= stickyBottom) return false;
      window.scrollTo({
        top: Math.max(window.scrollY + top - stickyBottom - 12, 0),
        behavior: this._prefersReducedMotion() ? "auto" : "smooth",
      });
      return true;
    }

    applyActiveCategory(opts) {
      const key = this.state.activeCategory;
      // Sections are <details>, so "which category is showing" is just which
      // one is `open`. On mobile the user drives that natively by tapping a
      // <summary>; here the category tabs drive the same state. Both ends meet
      // at this one property — no separate class or hidden attribute to keep
      // in step.
      // ...except when the user opened Add Ons by tapping its own summary. That
      // panel renders after every delivery, so closing the sections removes the
      // entire page above the row under their finger: the document collapses
      // (4.5k+ px with a full meal grid), the browser drops scrollY to 0, and
      // the surroundings whoosh past — read as the page "jumping". Add Ons is an
      // additive step rather than a replacement category, so the delivery
      // sections just stay as they were. The desktop category tabs, which do
      // mean "show me this instead", still switch exclusively.
      if (!opts?.keepSections) {
        this.root.querySelectorAll("[data-pb-section]").forEach((sectionEl) => {
          sectionEl.open = sectionEl.dataset.sectionKey === key;
        });
      }
      // The shared Add Ons panel is the "extras" category for every delivery.
      this.root.querySelectorAll("[data-pb-addons]").forEach((panel) => {
        panel.open = key === "extras";
      });
      this.root.querySelectorAll("[data-pb-category-tab]").forEach((tab) => {
        const active = tab.dataset.sectionKey === key;
        tab.classList.toggle("is-active", active);
        tab.setAttribute("aria-selected", active ? "true" : "false");
      });
      this.updateSectionCounts();
    }

    // The "(4/14)" tail on an open accordion header (Figma 12508:624). The
    // rest of the header — which label shows, which way the chevron points —
    // is pure CSS off the `open` attribute. Extras is uncapped, so it has no
    // count to show.
    updateSectionCounts() {
      this.root.querySelectorAll("[data-pb-section-count]").forEach((countEl) => {
        const sectionEl = countEl.closest("[data-pb-section]");
        if (!sectionEl) return;
        const cap = parseInt(sectionEl.dataset.cap, 10) || 0;
        if (cap <= 0) {
          countEl.setAttribute("hidden", "");
          return;
        }
        const di = parseInt(sectionEl.dataset.deliveryIndex, 10) || 0;
        countEl.removeAttribute("hidden");
        countEl.textContent = `(${this.sectionCount(di, sectionEl.dataset.sectionKey)}/${cap})`;
      });
    }

    updateDeliveryTabs() {
      this.state.deliveries.forEach((delivery, di) => {
        const badge = this.root.querySelector(`[data-pb-delivery-tab-progress][data-delivery-index="${di}"]`);
        if (!badge) return;
        const total = parseInt(badge.dataset.total, 10) || 0;
        const selected = ["breakfast", "meals", "snacks"].reduce(
          (sum, key) => sum + this.sectionCount(di, key), 0
        );
        badge.textContent = this.fmt("delivery_tab_progress", { selected, total });
        // Mobile pills carry a bare "9/9" twin of the badge.
        const shortBadge = this.root.querySelector(`[data-pb-delivery-tab-progress-short][data-delivery-index="${di}"]`);
        if (shortBadge) shortBadge.textContent = `${selected}/${total}`;
      });
    }

    updateSectionHeader() {
      const key = this.state.activeCategory;
      // The Add Ons panel carries its own heading and has nothing to filter.
      const bar = this.root.querySelector("[data-pb-section-bar]");
      if (bar) bar.hidden = key === "extras";
      const titleEl = this.root.querySelector("[data-pb-section-title]");
      if (!titleEl) return;
      // Use a clean category label (e.g. "Breakfasts"), not the collection
      // title — the collection is named with the plan prefix ("Trim & Slim …").
      // No count here: per-category progress lives on the category tabs.
      const name = this.i18n[`section_name_${key}`] || key;
      titleEl.textContent = this.fmt("section_title_nocount", { name });
    }

    // "selected/cap" badge on each capped category tab, always for the ACTIVE
    // delivery (caps differ between deliveries when their day counts differ).
    updateCategoryTabs() {
      const di = this.state.activeDeliveryIndex;
      this.root.querySelectorAll("[data-pb-category-tab-count]").forEach((badge) => {
        const key = badge.dataset.sectionKey;
        const sectionEl = this.root.querySelector(
          `[data-pb-delivery][data-delivery-index="${di}"] [data-pb-section][data-section-key="${key}"]`
        );
        const cap = sectionEl ? (parseInt(sectionEl.dataset.cap, 10) || 0) : 0;
        if (cap <= 0) {
          badge.setAttribute("hidden", "");
          return;
        }
        badge.removeAttribute("hidden");
        badge.textContent = `${this.sectionCount(di, key)}/${cap}`;
      });
    }

    populateDeliveryTabDates() {
      const dates = this.deliveryDates();
      const DS = window.DeliverySchedule;
      // "Sunday 16th August". Every read is in the store's frame — see
      // assets/delivery-schedule.js.
      const fmtDay = (d) => `${DS.formatDayOrdinal(d)} ${d.toLocaleDateString("en-GB", { month: "long", timeZone: "UTC" })}`;
      // Compact date for the mobile pills ("Sun 16 Aug").
      const fmtShort = (d) => DS.formatShort(d);
      // One delivery (3-day plan) means there is no second box to number
      // against, so the pill drops the "Box 1" prefix. Mirrors the Liquid in
      // snippets/plan-builder-delivery-tabs.liquid.
      const single = this.state.deliveries.length === 1;
      this.state.deliveries.forEach((_, di) => {
        const el = this.root.querySelector(`[data-pb-delivery-tab-date][data-delivery-index="${di}"]`);
        if (el) {
          el.textContent = single
            ? this.fmt("delivery_tab_label_single", { day: fmtDay(dates[di]) })
            : this.fmt("delivery_tab_label", { box: di + 1, day: fmtDay(dates[di]) });
        }
        const shortEl = this.root.querySelector(`[data-pb-delivery-tab-date-short][data-delivery-index="${di}"]`);
        if (shortEl) shortEl.textContent = fmtShort(dates[di]);
      });
    }

    // Required portions per delivery: breakfast = dayCount (if hasBreakfast),
    // meals = dayCount * mealsPerDay, snacks = dayCount (if hasSnacks).
    // Extras has no cap and never gates checkout. Sections whose collection
    // is empty (data gap) auto-satisfy — mirrors the reveal logic so the
    // flow doesn't stall on an unconfigured collection.
    deliveryComplete(di) {
      const d = this.state.deliveries[di];
      if (!d) return false;
      const sum = (key) => d.collections[key].selections.reduce((s, x) => s + x.qty, 0);
      const hasCards = (key) => {
        const sel = `[data-pb-delivery][data-delivery-index="${di}"] [data-pb-section][data-section-key="${key}"] [data-pb-card]`;
        return this.root.querySelector(sel) !== null;
      };
      const mealsCap = d.dayCount * this.state.mealsPerDay;
      if (hasCards("meals") && sum("meals") < mealsCap) return false;
      if (this.state.hasBreakfast && hasCards("breakfast") && sum("breakfast") < d.dayCount) return false;
      if (this.state.hasSnacks && hasCards("snacks") && sum("snacks") < d.dayCount) return false;
      return true;
    }

    isReadyForCheckout() {
      if (!this.state || !this.state.deliveries.length) return false;
      return this.state.deliveries.every((_, di) => this.deliveryComplete(di));
    }

    updateCheckoutCtaStates() {
      const ready = this.isReadyForCheckout();
      const targets = [
        this.root.querySelector("[data-pb-footer-continue]"),
        ...this.root.querySelectorAll("[data-pb-overview-continue]"),
      ];
      targets.forEach((el) => {
        if (!el) return;
        if (ready) {
          el.classList.remove("is-disabled");
          el.removeAttribute("aria-disabled");
        } else {
          el.classList.add("is-disabled");
          el.setAttribute("aria-disabled", "true");
        }
        // "Pick more | £x" while boxes still have room, "Continue | £x" once
     
        const label = el.querySelector("span:first-child");
        if (label) {
          label.textContent = ready ? this.i18n.continue : this.i18n.pick_more;
        }
      });
      // Bounce the footer CTA the moment the plan first becomes checkout-able
      // so the user notices Continue lighting up.
      if (ready && !this._ctaWasReady) {
        this.hapticBuzz();
        this.nudge(this.root.querySelector("[data-pb-footer-continue]"), "is-bouncing");
      }
      this._ctaWasReady = ready;
    }

    buildRechargeBundle(deliveryIndex) {
      const d = this.state.deliveries[deliveryIndex];
      const selections = [];
      ["breakfast", "meals", "snacks", "extras"].forEach((key) => {
        const col = d.collections[key];
        col.selections.forEach((s) => {
          if (!s.sellingPlanId) return; // skip lines without a selling plan
          selections.push({
            collectionId: col.collectionId,
            externalProductId: s.productId,
            externalVariantId: s.variantId,
            quantity: s.qty,
            sellingPlan: s.sellingPlanId,
          });
        });
      });
      return {
        externalProductId: d.bundle.productId,
        externalVariantId: d.bundle.variantId,
        selections,
      };
    }

    // The label stays as it is: no charge happens here, so wording like
    // "Processing..." would read as a payment. aria-busy swaps the arrow for a
    // spinner in CSS instead.
    setCheckoutCtasInFlight(inFlight) {
      const buttons = [
        ...this.root.querySelectorAll("[data-pb-footer-continue], [data-pb-overview-continue]"),
      ];
      if (inFlight) {
        buttons.forEach((b) => {
          b.classList.add("is-disabled");
          b.setAttribute("aria-busy", "true");
          b.setAttribute("aria-disabled", "true");
        });
      } else {
        buttons.forEach((b) => {
          b.removeAttribute("aria-busy");
        });
        this.updateCheckoutCtaStates();
      }
    }

    openErrorModal(message) {
      const modal = this.root.querySelector("[data-pb-error-modal]");
      const msgEl = this.root.querySelector("[data-pb-error-message]");
      if (msgEl) msgEl.textContent = message || this.i18n.checkout_error_generic;
      if (modal) modal.removeAttribute("hidden");
    }

    closeErrorModal() {
      const modal = this.root.querySelector("[data-pb-error-modal]");
      if (modal) modal.setAttribute("hidden", "");
    }

    openInfoModal(btn) {
      const card = btn.closest("[data-pb-card], [data-pb-addon-card]");
      const modal = this.root.querySelector("[data-pb-info-modal]");
      const body = modal && modal.querySelector("[data-pb-info-body]");
      const tpl = card && card.querySelector("[data-pb-info-template]");
      if (!modal || !body || !tpl) return;
      body.innerHTML = "";
      body.appendChild(tpl.content.cloneNode(true));
      modal.removeAttribute("hidden");
      document.body.style.overflow = "hidden";
      const closeBtn = modal.querySelector("[data-pb-info-close]");
      if (closeBtn) closeBtn.focus();
    }

    closeInfoModal() {
      const modal = this.root.querySelector("[data-pb-info-modal]");
      if (!modal || modal.hasAttribute("hidden")) return;
      modal.setAttribute("hidden", "");
      const body = modal.querySelector("[data-pb-info-body]");
      if (body) body.innerHTML = "";
      document.body.style.overflow = "";
    }

    // Transient toast for blocked actions (section cap hit, variant out of
    // stock). HTML is allowed in `html` so callers can pass <strong> from
    // _html locale values.
    showToast(html) {
      const toast = this.root.querySelector("[data-pb-toast]");
      const msg = this.root.querySelector("[data-pb-toast-message]");
      if (!toast || !msg) return;
      msg.innerHTML = html;
      toast.removeAttribute("hidden");
      if (this._toastTimer) clearTimeout(this._toastTimer);
      // Force reflow so the in animation re-triggers when the same toast
      // fires twice in a row.
      toast.classList.remove("is-visible");
      void toast.offsetWidth;
      toast.classList.add("is-visible");
      this._toastTimer = setTimeout(() => {
        toast.classList.remove("is-visible");
        this._toastTimer = setTimeout(() => toast.setAttribute("hidden", ""), 250);
      }, 3000);
    }

    async handleCheckout() {
      if (!this.isReadyForCheckout()) return;
      if (this._checkoutInFlight) return;
      if (!window.recharge || !window.recharge.bundle || typeof window.recharge.bundle.getDynamicBundleItems !== "function") {
        console.error(
          "[plan-builder] window.recharge.bundle.getDynamicBundleItems is not available.",
          { rechargeLoaded: !!window.recharge, bundleApi: !!(window.recharge && window.recharge.bundle) },
        );
        this.openErrorModal(this.i18n.checkout_error_generic);
        return;
      }
      // An Add On without a selling plan would be dropped by buildRechargeBundle()
      // after the customer was shown its price. Stop rather than under-charge.
      const addons = this.addonsModel();
      const uncharged = addons ? addons.missingSellingPlan(this.state.deliveries) : [];
      if (uncharged.length) {
        console.error("[plan-builder] Add Ons without a selling plan", uncharged);
        this.openErrorModal(this.i18n.checkout_error_generic);
        return;
      }
      // Meals carry their share of the delivery fee in their plan-tier variant. A
      // tiered menu with no tier means the plan config is missing or is one the
      // catalogue has no variants for (the cards fell back to Standard prices), so
      // this plan would ship with no delivery contribution at all.
      const tieredMenu = this.root.querySelector(
        '[data-pb-section][data-section-key="meals"] [data-pb-card][data-variant-ids]',
      );
      if (tieredMenu && !this.config.planTier) {
        console.error("[plan-builder] no plan tier for this config", {
          days: this.config.daysPerWeek, meals: this.config.mealsPerDay,
        });
        this.openErrorModal(this.i18n.no_plan_body || this.i18n.checkout_error_generic);
        return;
      }
      // Same for the dishes themselves: a meal dropped there is counted, priced and
      // then missing from the box, and it takes its share of the delivery fee with it.
      const unchargedDishes = this.state.deliveries.flatMap((delivery, di) =>
        ["breakfast", "meals", "snacks"].flatMap((key) =>
          delivery.collections[key].selections
            .filter((s) => !s.sellingPlanId)
            .map((s) => ({ delivery: di, section: key, variantId: s.variantId, title: s.productTitle })),
        ),
      );
      if (unchargedDishes.length) {
        console.error("[plan-builder] dishes without a selling plan", unchargedDishes);
        this.openErrorModal(this.i18n.checkout_error_generic);
        return;
      }
      if (!this.confirmDeliveryDateOffered()) return;
      this._checkoutInFlight = true;
      this.setCheckoutCtasInFlight(true);
      try {
        // A delivery-date write started moments ago (a pick, or the on-load
        // default) must land before the cart is rebuilt and the page leaves.
        await this._deliveryDateWrite;
        const bundles = this.state.deliveries.map((_, i) => this.buildRechargeBundle(i));

        const perDelivery = await Promise.all(
          bundles.map((b) => window.recharge.bundle.getDynamicBundleItems(b, "shopifyProductHandle"))
        );
        perDelivery.forEach((list, i) => {
          (list || []).forEach((it) => {
            it.properties = Object.assign({}, it.properties, {
              _cr_delivery: String(i),
              _delivery_day: i === 0 && this.state.deliveryDay !== "wednesday" ? "Sunday" : "Wednesday",
            });
          });
          // _cr_addon + _delivery_date let the order and fulfilment tell which box
          // each Add On is packed in (the review page reads _cr_addon too).
          if (addons) {
            const iso = i === 0 ? this.state.firstDeliveryDate : this.state.secondDeliveryDate;
            addons.tagCheckoutLines(list, this.state.deliveries, i, iso);
          }
        });
        await this.clearExistingPlan();
        await this.addNestedPlanLines(perDelivery);
        await this.finishCheckout();
      } catch (err) {
        console.error("[plan-builder] checkout failed", err);
        const isNetwork = err instanceof TypeError || /NetworkError|Failed to fetch/i.test(String(err && err.message));
        this.openErrorModal(
          isNetwork
            ? this.i18n.checkout_error_network
            : (err && err.message ? err.message : this.i18n.checkout_error_generic)
        );
        this._checkoutInFlight = false;
        this.setCheckoutCtasInFlight(false);
      }
    }

   
    async addNestedPlanLines(perDelivery) {
      const parents = [];
      this.state.deliveries.forEach((delivery, i) => {
        const bundleMeta = delivery.bundle;
        if (!bundleMeta || !bundleMeta.variantId) return;
        const line = {
          id: bundleMeta.variantId,
          quantity: 1,
          properties: { _cr_delivery: String(i), _cr_plan_parent: "1" },
        };
        if (bundleMeta.sellingPlanId) line.selling_plan = bundleMeta.sellingPlanId;
        parents.push(line);
      });

      const keyByDelivery = new Map();
      if (parents.length) {
        // Shopify puts each newly added line at the top of the cart, so a
        // [Sunday, Wednesday] add lands Wednesday-first at checkout
       
        const added = await this.cartAdd(parents.slice().reverse());
        ((added && added.items) || []).forEach((line) => {
          const props = line.properties || {};
          if (props._cr_plan_parent && props._cr_delivery != null) {
            keyByDelivery.set(String(props._cr_delivery), line.key);
          }
        });
        if (keyByDelivery.size === 0) {
          console.warn(
            "[plan-builder] parent lines added but no keys resolved — meals will go in un-nested.",
            added,
          );
        }
      }

      const children = [];
      perDelivery.forEach((list, i) => {
        const parentKey = keyByDelivery.get(String(i));
        (list || []).forEach((it) => {
          children.push(parentKey ? Object.assign({}, it, { parent_line_key: parentKey }) : it);
        });
      });

      if (children.length) {
        const addedChildren = await this.cartAdd(children);
        const nestedCount = ((addedChildren && addedChildren.items) || []).filter(
          (l) => l.parent_relationship,
        ).length;
        console.info(
          `[plan-builder] nested ${nestedCount}/${children.length} meal lines under the plan.`,
        );
      }
    }

    async finishCheckout() {
      // The bundle line items are now the source of truth, so the working-state
      // attribute has done its job. It must not survive into checkout: Shopify
      // coerces a JSON attribute to Ruby-hash text there, and a 1-2 KB blob has no
      // business on the merchant's order.
      //
      // Cancel any pending debounced save FIRST. A trailing write landing after this
      // clear would resurrect the attribute and carry it into checkout.
      clearTimeout(this._saveTimer);
      this._saveTimer = null;

      // The delivery dates ride on this last write too. Shopify can drop a cart
      // write that overlaps another one (the on-load date save racing the
      // buying journey's last answer or the plan sync), and nothing after this
      // write touches the cart. So the cart it returns must carry the dates:
      // one retry, then stop rather than send the customer to checkout
      // without a delivery date.
      const dates = this.deliveryDateAttributes();
      const attributes = { selections: "", ...dates };
      const DS = window.DeliverySchedule;
      let cart = await this.writeCartAttributes(attributes);
      if (!cart || !DS.cartHasDates(cart.attributes, dates)) {
        console.warn("[plan-builder] delivery dates missing from the cart, saving again", cart && cart.attributes);
        cart = await this.writeCartAttributes(attributes);
      }
      if (!cart || !DS.cartHasDates(cart.attributes, dates)) {
        throw new Error(this.i18n.delivery_date_save_failed || this.i18n.checkout_error_generic);
      }

      window.location.href = window.routes.cart_url;
    }

    // POST /cart/update.js; the updated cart, or null when the write failed.
    async writeCartAttributes(attributes) {
      const campaign = window.GoodPrepCampaignOffer;
      try {
        const res = await fetch(`${window.routes.cart_update_url}.js`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          // The campaign code and ID ride along too: the on-load write that set
          // them can be dropped if it overlapped another (see finishCheckout).
          body: JSON.stringify(campaign
            ? { attributes: { ...attributes, klaviyo_campaign_id: campaign.id }, discount: campaign.code }
            : { attributes }),
        });
        if (!res.ok) throw new Error(`cart/update ${res.status}`);
        return await res.json();
      } catch (err) {
        console.error("[plan-builder] cart attribute write failed", err);
        return null;
      }
    }

    // A date picked before its order deadline passed (the page was left open)
    // is no longer deliverable. Move it to the next option on the same weekday
    // and let the customer see it before anything reaches the cart: the error
    // modal's Retry runs checkout again with the new date.
    confirmDeliveryDateOffered() {
      const DS = window.DeliverySchedule;
      if (!DS) {
        this.openErrorModal(this.i18n.checkout_error_generic);
        return false;
      }
      const options = this.deliveryOptions();
      const chosen = DS.fromISODate(this.state.firstDeliveryDate);
      if (!options.length || DS.isOneOf(chosen, options)) return true;
      const next = DS.replacementDate(chosen, options);
      this.applyDeliveryDate(DS.toISODate(next));
      this.openErrorModal(
        (this.i18n.delivery_date_moved || "").replace("__DATE__", DS.formatLong(next)) ||
          this.i18n.checkout_error_generic,
      );
      return false;
    }

    async cartAdd(items) {
      const res = await fetch(`${window.routes.cart_add_url}.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) {
        let detail = "";
        try {
          const body = await res.json();
          detail = body.description || body.message || "";
        } catch (e) { /* ignore parse errors */ }
        throw new Error(detail || `${res.status}`);
      }
      return res.json();
    }

  
  
    // Now lives in the shared assets/plan-cart.js: the resume dialog's "Start a new
    // plan" has to do exactly this, and two copies of cart surgery would drift. Kept as
    // a method so the checkout call sites above read unchanged.
    async clearExistingPlan() {
      await window.PlanCart.clearPlanLines();
    }

    updateFooter() {
      const di = this.state.activeDeliveryIndex;
      const mealKeys = ["breakfast", "meals", "snacks"];
      const mealCount = mealKeys.reduce((sum, key) => sum + this.sectionCount(di, key), 0);
      const mealsEl = this.root.querySelector("[data-pb-meals-count]");
      if (mealsEl) mealsEl.textContent = String(mealCount);
      // The delivery dates in the footer are owned by initDeliveryDate() /
      // applyDeliveryDate(), which repaint every date label together.
    }

    // ===== Delivery dates (TSK-10772) =====
    // The customer picks the FIRST box's date from the calendar; everything
    // else follows from it. delivery-schedule.js owns the maths (cutoff,
    // store-time frame, which days are offered); this controller owns the
    // choice, the labels, the bundle it implies and the cart attributes.

    // Every date the customer may pick for the first box. Wednesday is only
    // offered on single-box plans: a two-box plan's second box is the
    // Wednesday three days after the first, which needs a Sunday start.
    deliveryOptions() {
      const DS = window.DeliverySchedule;
      return DS.firstDeliveryOptions(this.config.cutoff, DS.parseOffsetMinutes(this.config.storeNow), {
        weeksAhead: this.config.weeksAhead,
        allowWednesday: this.state.deliveries.length === 1,
      });
    }

    // Restores the saved pick from the cart when it is still deliverable (the
    // cutoff may have moved past it, or the plan may have changed from one box
    // to two), otherwise defaults to the earliest option. The result is written
    // back either way: a customer who never opens the calendar must still
    // reach checkout with a date on the cart.
    initDeliveryDate() {
      const DS = window.DeliverySchedule;
      if (!DS) {
        console.error("[plan-builder] delivery-schedule.js not loaded; delivery dates not rendered");
        return;
      }
      const options = this.deliveryOptions();
      if (!options.length) return;
      const attrs = this.readCartAttributes();
      // The first box is the Sunday (batch 1) when the plan has one, else the
      // Wednesday (batch 2) of a single-box plan. Only the current
      // configuration's key is ever non-blank, so batch 1 is safe to prefer.
      const saved = DS.fromISODate(
        attrs.batch_1_delivery_date || attrs.batch_2_delivery_date || attrs.delivery_date
      );
      const chosen = DS.isOneOf(saved, options) ? saved : options[0];
      this.applyDeliveryDate(DS.toISODate(chosen));
    }

    // Single writer for the delivery date: state, the bundle the first box
    // checks out with, every date label on the page, and the cart attributes.
    applyDeliveryDate(iso) {
      const DS = window.DeliverySchedule;
      const first = DS.fromISODate(iso);
      if (!first) return;
      const twoBoxes = this.state.deliveries.length === 2;
      this.state.firstDeliveryDate = iso;
      this.state.secondDeliveryDate = twoBoxes ? DS.toISODate(DS.secondBoxDate(first)) : null;
      this.state.deliveryDay = first.getUTCDay() === 3 ? "wednesday" : "sunday";

      // A Wednesday first box ships on the plan's Wednesday bundle and selling
      // plan (recharge_bundle_product_delivery_2), so Recharge charges on the
      // right cadence. Fall back to whatever the delivery started with if the
      // plan has no bundle for that day.
      const bundles = this.config.bundles || {};
      const bundle = bundles[this.state.deliveryDay];
      if (bundle && bundle.variantId) this.state.deliveries[0].bundle = bundle;

      this.refreshDeliveryDateLabels();
      this.emit();
      this.persistDeliveryDate();
    }

    refreshDeliveryDateLabels() {
      const datesEl = this.root.querySelector("[data-pb-dates]");
      if (datesEl) datesEl.textContent = this.computeDatesLabel();
      this.populateOverviewDates();
      this.populateDeliveryTabDates();
    }

    openDatePicker() {
      const picker = this.root.querySelector("[data-pb-date-picker]");
      if (!picker || typeof picker.open !== "function" || !window.DeliverySchedule) return;
      picker.open({
        options: this.deliveryOptions(),
        selected: this.state.firstDeliveryDate,
        onConfirm: (iso) => this.applyDeliveryDate(iso),
      });
    }

    // The batch_N_delivery_date attributes for the chosen dates.
    deliveryDateAttributes() {
      const DS = window.DeliverySchedule;
      return DS.cartAttributes([
        DS.fromISODate(this.state.firstDeliveryDate),
        DS.fromISODate(this.state.secondDeliveryDate),
      ]);
    }

    // Cart attributes carry the choice to checkout, where the order tag and
    // the Recharge subscription read them — see DeliverySchedule.cartAttributes:
    // batch_1_delivery_date (the Sunday run) and batch_2_delivery_date (the
    // Wednesday run), each set only when the plan has a box in that batch.
    persistDeliveryDate() {
      const attributes = this.deliveryDateAttributes();
      const current = this.readCartAttributes();
      LEGACY_DELIVERY_ATTRIBUTE_KEYS.forEach((key) => {
        if (current[key]) attributes[key] = "";
      });
      const unchanged = Object.keys(attributes).every((key) => (current[key] || "") === attributes[key]);
      if (unchanged) return Promise.resolve();
      this.patchCartAttributesSnapshot(attributes);
      // Kept on the instance so handleCheckout() can wait for it. Always
      // resolves — a failed save is logged, never allowed to block checkout.
      this._deliveryDateWrite = fetch(`${window.routes.cart_update_url}.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ attributes }),
      })
        .then((res) => {
          if (!res.ok) throw new Error(`cart/update ${res.status}`);
        })
        .catch((err) => console.error("[plan-builder] failed to save delivery date", err));
      return this._deliveryDateWrite;
    }

    // The section renders cart.attributes into [data-pb-cart-attributes] so
    // nothing here has to fetch /cart.js. Visitor-writable, so treat as input.
    readCartAttributes() {
      const node = this.root.querySelector("[data-pb-cart-attributes]");
      if (!node) return {};
      try {
        return JSON.parse(node.textContent.trim()) || {};
      } catch (e) {
        return {};
      }
    }

    // Keeps that snapshot in step with what was just written, so a later read
    // in the same page life (another pick, the deep-link reconciler) sees it.
    patchCartAttributesSnapshot(patch) {
      const node = this.root.querySelector("[data-pb-cart-attributes]");
      if (!node) return;
      node.textContent = JSON.stringify(Object.assign({}, this.readCartAttributes(), patch));
    }

    // [first box, second box]. Before initDeliveryDate() has run (or if it
    // could not), fall back to the earliest Sunday so labels never read blank.
    deliveryDates() {
      const DS = window.DeliverySchedule;
      const first = DS.fromISODate(this.state.firstDeliveryDate) || this.nextDeliverySunday();
      return [first, DS.secondBoxDate(first)];
    }

    computeDatesLabel() {
      const [first, second] = this.deliveryDates();
      const fmt = (d) => window.DeliverySchedule.formatShort(d);
      if (this.state.deliveries.length === 1) {
        return this.fmt("footer_delivery_on", { date: fmt(first) });
      }
      return this.fmt("footer_deliveries_on", { sun: fmt(first), wed: fmt(second) });
    }

    // "Sunday, 13 September" on the delivery-date cards.
    populateOverviewDates() {
      const dates = this.deliveryDates();
      const fmtLong = (d) => window.DeliverySchedule.formatLongComma(d);
      this.state.deliveries.forEach((_, di) => {
        // querySelectorAll — one label per rendered overview (two on mobile).
        this.root
          .querySelectorAll(`[data-pb-overview-date][data-delivery-index="${di}"] [data-pb-overview-date-label]`)
          .forEach((label) => { label.textContent = fmtLong(dates[di]); });
      });
    }

    // The cutoff day (config.cutoff.day) gates which Sunday a customer receives.
    // Orders placed before 22:00 STORE time on that day deliver the upcoming
    // Sunday; later orders skip it and slide to the following week's Sunday.
    // See assets/delivery-schedule.js for the fixed cutoff time, and for why
    // the store's UTC offset is threaded through instead of the shopper's clock.
    nextDeliverySunday() {
      return window.DeliverySchedule.nextDeliverySunday(
        this.config.cutoff,
        window.DeliverySchedule.parseOffsetMinutes(this.config.storeNow)
      );
    }

    expose() {
      window.PlanBuilder = window.PlanBuilder || {};
      window.PlanBuilder.state = this.state;
      window.PlanBuilder.getState = () => JSON.parse(JSON.stringify(this.state));
    }

    emit() {
      this.root.dispatchEvent(new CustomEvent("plan-builder:state", {
        bubbles: true,
        detail: { state: this.state },
      }));
    }
  }

  window.__PlanBuilderController = PlanBuilderController;

  const init = (scope = document) => {
    scope.querySelectorAll("[data-plan-builder]").forEach((root) => {
      if (root.__controller) return;
      root.__controller = new PlanBuilderController(root);
    });
  };

  // The deep-link reconciler replaces this section's markup wholesale and needs to
  // bring the controller back up on the new nodes.
  window.__PlanBuilderInit = init;

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => init());
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", (e) => init(e.target));
  document.addEventListener("shopify:section:unload", (e) => {
    const root = e.target.querySelector("[data-plan-builder]");
    if (root?.__controller) {
      root.__controller.destroy();
      root.__controller = null;
    }
  });
}
